import { Column } from 'common/graphqlTypes';
import { getTextWidth } from 'service/browser';
import cssVariables from 'styles/_export.module.scss';
import { AVERAGE_NUMBER_OF_ROWS, LEFT_AND_RIGHT_PADDINGS, MAX_COL_WIDTH, MIN_COL_WIDTH } from './consts';
import { ColumnFormat, Values } from './types';

// Giving it a "hopefully" unique ID that can be used as the internal React Base row key
export const ID_COLUMN_KEY = '_dataSetReactBaseTableId';

/**
 * Helper function that maps data that has been fetched for API to react-base-table requirements
 *
 * @param data        table data that has been fetched from API
 * @param headers     table headers
 *
 * @return            array of key value object, where key is a header name
 */
export const prepareData = (data: Values, headers: Column[]) =>
  data.map<Record<string, string>>((row, rowIndex) => ({
    [ID_COLUMN_KEY]: rowIndex,
    ...row.reduce((accumulator, currentValue, index) => {
      accumulator[headers[index].name] = currentValue;
      return accumulator;
    }, {}),
  }));

/**
 * Helper function which computes the longest row in the table in px
 * @param headers     table headers
 * @param rowValues   table row values fetched from API

 *
 * @return            key value object where key is a header name and value it's length in px
 */
export const getColumnsWidth = (headers: Column[] = [], rowValues: Values = []): Record<string, number> => {
  const headersArray: Array<string> = [];

  const lengths: Record<string, number> = headers.reduce(
    (accumulator: Record<string, number>, { name }): Record<string, number> => {
      const headerWidth =
        (getTextWidth(name, `${cssVariables.font_sm} ${cssVariables.monospaced_font}`) || 0) + LEFT_AND_RIGHT_PADDINGS;

      const minimumColumnWidth = Math.max(MIN_COL_WIDTH, headerWidth);

      if (name === ColumnFormat.TIMESTAMP) {
        accumulator.timestamp = 200;
      } else {
        accumulator[name] = minimumColumnWidth;
      }

      headersArray.push(name);

      return accumulator;
    },
    {},
  );

  const numberOfIterations = Math.min(rowValues.length, AVERAGE_NUMBER_OF_ROWS);

  for (let i = 0; i < numberOfIterations; i += 1) {
    for (let k = 0; k < headersArray.length; k += 1) {
      const header = headersArray[k];
      const element = rowValues[i][k];

      const stringLengthInPX =
        (getTextWidth(element, `${cssVariables.font_sm} ${cssVariables.monospaced_font}`) || 0) +
        LEFT_AND_RIGHT_PADDINGS;

      lengths[header] = Math.min(Math.max(stringLengthInPX, lengths[header]), MAX_COL_WIDTH);
    }
  }

  return lengths;
};

/**
 * Removes the given columns from PQ results columns and values.
 *
 * @param columns            PQ result columns
 * @param values             PQ result values (list of lists)
 * @param columnsToRemove    list of names of columns to remove
 *
 * @returns updated columns and values
 */
export const removeColumns = (
  columns: Column[],
  values: any[][],
  columnsToRemove: string[],
): { columns: Column[]; values: any[][] } => {
  const keepColumn = columns.map((column) => !columnsToRemove.includes(column.name));
  const newColumns = columns.filter((column, index) => keepColumn[index]);
  const newValues = values.map((row) => row.filter((val, index) => keepColumn[index]));

  return { columns: newColumns, values: newValues };
};
